import { type ReactNode } from 'react'

import { InhertaIcon, InhertaWordmark } from '../../design-system/brand/InhertaMark'

// The two-panel auth frame (design/look-and-feel/10-entry-login.html): a branded
// left panel (always dark, like the app sidebar) + the form on the right.
// Shared by Login / Mfa / password-reset so the entry experience is consistent.

function HelpIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.1}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3.5"
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M9.2 9.3a2.9 2.9 0 0 1 5.6 1c0 1.9-2.8 2.9-2.8 2.9M12 17.3h.01" />
    </svg>
  )
}

export interface AuthLayoutProps {
  children: ReactNode
  /** Optional notice rendered under the form (e.g. the concierge access note). */
  notice?: ReactNode
}

export function AuthLayout({ children, notice }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen bg-bg">
      {/* Brand panel — always dark, hidden below md (phones see the inline mark). */}
      <aside className="relative hidden w-[44%] max-w-[520px] flex-col justify-between overflow-hidden bg-chrome p-12 md:flex">
        <div className="relative z-10 flex items-center">
          <InhertaWordmark className="h-[22px] w-auto text-white" />
        </div>

        <div className="relative z-10">
          <p className="text-[clamp(27px,2.6vw,34px)] font-extrabold leading-[1.13] text-white">
            Ninguna conversación se queda{' '}
            <em className="bg-[linear-gradient(96deg,#5f9dff,#0a68ff)] bg-clip-text not-italic text-transparent">
              sin avanzar
            </em>
            .
          </p>
          <p className="mt-4 max-w-[30ch] text-[14px] leading-relaxed text-[#aab2c2]">
            Transformamos conversaciones en resultados, para hospitales y negocios de México.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-[12px] font-medium text-[#aab2c2]">
          <span className="size-1.5 rounded-full bg-[#0a68ff]" />
          Plataforma de agentes conversacionales
        </div>

        <svg
          viewBox="0 0 460 320"
          fill="none"
          aria-hidden
          className="pointer-events-none absolute -right-16 bottom-10 w-[460px]"
        >
          <defs>
            <linearGradient
              id="auth-flowbig"
              x1="14"
              y1="300"
              x2="446"
              y2="58"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#0a68ff" stopOpacity="0.25" />
              <stop offset="1" stopColor="#0a68ff" />
            </linearGradient>
          </defs>
          <path
            d="M14 300C120 300 140 150 250 168C340 183 372 70 446 58"
            stroke="url(#auth-flowbig)"
            strokeWidth="6"
            strokeLinecap="round"
            opacity="0.5"
          />
          <circle cx="14" cy="300" r="9" fill="#0a68ff" fillOpacity="0.45" />
          <circle cx="250" cy="168" r="6.5" fill="#0a68ff" fillOpacity="0.7" />
          <circle cx="446" cy="58" r="11" fill="var(--chrome)" stroke="#0a68ff" strokeWidth="6" />
        </svg>
      </aside>

      {/* Form panel */}
      <div className="flex flex-1 flex-col px-6 py-8">
        <div className="flex justify-end">
          <a
            href="#"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-text-2 transition-colors hover:text-link [&_svg]:text-text-3"
          >
            <HelpIcon />
            ¿Necesitas ayuda?
          </a>
        </div>
        <div className="m-auto w-full max-w-[362px]">
          {/* Phones never see the brand panel, so the mark comes inline here.
              The isotipo rather than the wordmark: it is the same mark, and it
              does not compete with the form heading right below it. */}
          <div className="mb-7 md:hidden">
            <InhertaIcon className="h-[30px] w-auto" />
          </div>
          {children}
          {notice}
        </div>
      </div>
    </div>
  )
}
